import React, { FC, useState, useMemo } from 'react'
import { CSSObject } from '@emotion/react'
import { useTranslate, TranslationKeys } from '@ds/comp-private'
import { Menu, useDisclosure } from '@ds/ui'
import { Locale } from '@ds/base'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min'
import type {
  CallingCode,
  CountryCode,
} from '../FormattedTextBox/formatters/createPhoneNumberFormatter'
import { DebouncedTextBox } from './DebouncedTextBox'
import {
  noMatchesContainer,
  searchInputContainer,
  preferredListContainer,
  countryListContainer,
} from './styles'
import { MenuTrigger } from './MenuTrigger'
import { FREEFORM_CODE, FreeformCode, PhoneInputSize } from './types'

export interface Country {
  /** A two-letter abbreviation for the country. See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 */
  countryCode: string
  /** A string of 1, 2, or 3 numeric digits. It represents a country or region where the phone number is assigned. */
  callingCode: string
  /** The full name of the country. */
  name: string
  /** The full name of the country along with its calling code in parentheses. */
  displayText: string
}

export interface CallingCodeMenuProps {
  /** The currently selected calling code. */
  value: string
  /** Fired when the user makes a new selection. */
  onCountrySelect: (country: Country) => void
  /** Displayed at the top of the calling code menu. These can be provided as 2-digit ISO country codes (e.g. "US"), country calling codes (e.g. "1"), or a mix of both. */
  preferredCountryCodes?: (CountryCode | CallingCode | FreeformCode)[]
  /**
   * Excludes the provided countries from the built-in calling code menu.
   */
  excludedCountryCodes?: CountryCode[]
  /** Identifier for automated testing. */
  'data-qa'?: string
  /** Custom data-qa values for elements of this component. Overrides the data-qa prop. */
  qa?: {
    menuTrigger?: string
    menuContainer?: string
    searchInput?: string
    getMenuItem?: (item: { countryCode: string; callingCode: string }) => string
  }
  /** Allows the user to go into a freeform mode by selecting the option from the callingCodeMenu. */
  allowFreeform: boolean
  /** Overrides the default text for the freeform option. */
  freeformOption?: {
    optionText: string
    selectedText: string
  }
  /** Forces selection of the freeform option.  */
  freeformSelected: boolean
  /** Disables the trigger button.  */
  disabled?: boolean
  /** Reference to an HTML element which will be used as the anchor for the menu. By default the trigger button is used. */
  anchorRef?: React.RefObject<HTMLElement>
  readOnly?: boolean
  /** Optional locale to override the default lang on the page */
  locale?: Locale
  /** Size of the calling code menu trigger + search input */
  size?: PhoneInputSize
}

/**
 * Renders an trigger and popup menu. The menu is searchable list of calling codes.
 *
 * @param props
 */
export const CallingCodeMenu: FC<CallingCodeMenuProps> = ({
  value,
  onCountrySelect,
  preferredCountryCodes = [],
  excludedCountryCodes,
  'data-qa': data_qa,
  qa,
  allowFreeform,
  freeformOption,
  freeformSelected,
  disabled = false,
  anchorRef,
  readOnly,
  locale,
  size,
}) => {
  const translate = useTranslate(locale)
  const [searchText, setSearchText] = useState('')
  const [triggerElement, setTriggerElement] =
    useState<HTMLButtonElement | null>(null)
  const [disclosureElement, setDisclosureElement] =
    useState<HTMLDivElement | null>(null)

  const { closeDisclosure, disclosureProps, triggerProps } = useDisclosure({
    disclosureElement,
    triggerElement,
    options: {
      closeOnEscape: true,
      closeOnFocusLoss: true,
      closeOnOutsideClick: true,
    },
  })

  const freeformCountry: Country = useMemo(() => {
    if (freeformOption) {
      return {
        name: 'Freeform',
        countryCode: FREEFORM_CODE,
        displayText: freeformOption.optionText,
        callingCode: freeformOption.selectedText,
      }
    }
    return {
      name: 'Freeform',
      countryCode: FREEFORM_CODE,
      displayText: translate('PHONE_INPUT:FREEFORM_OPTION'),
      callingCode: translate('PHONE_INPUT:FREEFORM_SELECTED'),
    }
  }, [freeformOption, translate])

  const noMatchesText = useMemo(
    () => translate('PHONE_INPUT:EMPTY_SEARCH_RESULT'),
    [translate]
  )

  const countries = useMemo(() => {
    const _countries: Country[] = []

    const excluded = ['CU', 'IR', 'KP', 'SY', ...(excludedCountryCodes ?? [])]
    const filteredCountries = getCountries().filter(
      (code) => !excluded.includes(code)
    )

    filteredCountries.forEach((countryCode) => {
      const callingCode = getCountryCallingCode(countryCode) as string
      const name = translate(`CTY_${countryCode}` as TranslationKeys)
      const country: Country = {
        countryCode,
        callingCode,
        name,
        displayText: `${name} (+${callingCode})`,
      }
      _countries.push(country)
    })

    if (allowFreeform) {
      _countries.push(freeformCountry)
    }

    return _countries.sort(({ displayText: a }, { displayText: b }) =>
      new Intl.Collator().compare(a, b)
    )
  }, [translate, allowFreeform, freeformCountry, excludedCountryCodes])

  const { searchedCountries, preferredCountries } = useMemo(() => {
    return searchHits(countries, searchText, preferredCountryCodes)
  }, [countries, searchText, preferredCountryCodes])

  const handleSelection = (country: Country) => () => {
    onCountrySelect(country)
    closeDisclosure?.()
  }

  return (
    <>
      <MenuTrigger
        {...triggerProps}
        disabled={disabled || readOnly}
        aria-label={`${translate('PHONE_INPUT:TRIGGER_ARIA_LABEL')} ${value}`}
        size={size}
        onClick={() => {
          setSearchText('')
          triggerProps.onClick()
        }}
        forwardedRef={setTriggerElement as React.Ref<HTMLButtonElement>}
        data-qa={qa?.menuTrigger || (data_qa && data_qa + '-menu-trigger')}
        showCaret={true}
      >
        {freeformSelected ? freeformCountry.callingCode : `+${value}`}
      </MenuTrigger>
      <Menu
        {...disclosureProps}
        ariaPattern="disclosure"
        data-qa={qa?.menuContainer || (data_qa && data_qa + '-menu')}
        locationFixed={true}
        location="below"
        anchor={anchorRef?.current || triggerElement}
        forwardedRef={setDisclosureElement}
        maxHeight="400px"
      >
        <Menu.Group title={translate('PHONE_INPUT:COUNTRY_SELECT_HEADING')}>
          <Menu.CustomItem>
            <div css={searchInputContainer}>
              <DebouncedTextBox
                data-qa={qa?.searchInput || (data_qa && data_qa + '-search')}
                label={translate('PHONE_INPUT:SEARCH_LABEL')}
                delay={100}
                delayedOnChange={setSearchText}
                size={size}
              />
            </div>
          </Menu.CustomItem>
          {preferredCountries.length > 0 && (
            <MenuItemsContainer cssObject={preferredListContainer}>
              {preferredCountries.map((country) => (
                <Menu.Item
                  key={country.countryCode}
                  text={country.displayText}
                  onClick={handleSelection(country)}
                />
              ))}
            </MenuItemsContainer>
          )}
          <MenuItemsContainer cssObject={countryListContainer}>
            {searchedCountries.map((country) => {
              const { countryCode, callingCode, displayText } = country
              return (
                <Menu.Item
                  data-qa={
                    qa?.getMenuItem
                      ? qa.getMenuItem({ countryCode, callingCode })
                      : data_qa && data_qa + '-item-' + countryCode
                  }
                  key={countryCode}
                  text={displayText}
                  onClick={handleSelection(country)}
                />
              )
            })}
            {searchedCountries.length === 0 &&
              preferredCountries.length === 0 && (
                <div css={noMatchesContainer}>{noMatchesText}</div>
              )}
          </MenuItemsContainer>
        </Menu.Group>
      </Menu>
    </>
  )
}

/**
 * Menu passes custom props to its children, which should not be received by DOM elements.
 * MenuItemsContainer is needed to avoid warnings from React.
 */
const MenuItemsContainer = ({
  children,
  cssObject,
}: {
  children: React.ReactNode
  cssObject: CSSObject
}) => {
  return <div css={cssObject}>{children}</div>
}

export const isPreferred = (
  { countryCode, callingCode }: Country,
  preferredList: string[]
): boolean => {
  return preferredList.some(
    (preferred) =>
      preferred.toLowerCase() === countryCode.toLowerCase() ||
      preferred === callingCode
  )
}

// Strips diacritical marks (e.g. ü → u) so that searches like "tur" match "Türkiye"
export const normalizeDiacritics = (str: string) =>
  str.normalize('NFD').replace(/\p{Diacritic}/gu, '')

export const isSearchedFor = (
  displayText: string,
  searchText: string
): boolean => {
  return (
    !searchText ||
    normalizeDiacritics(displayText)
      .toLocaleLowerCase()
      .includes(normalizeDiacritics(searchText).toLocaleLowerCase())
  )
}

export function startsWith(displayText: string, searchText: string): boolean {
  return normalizeDiacritics(displayText)
    .toLocaleLowerCase()
    .startsWith(normalizeDiacritics(searchText).toLocaleLowerCase())
}

export function searchHits(
  countries: Country[],
  searchText: string,
  preferredCountryCodes: string[]
) {
  const _searchedCountriesStartsWith: Country[] = []
  const _searchedCountries: Country[] = []
  const _preferredCountriesStartsWith: Country[] = []
  const _preferredCountries: Country[] = []

  countries.forEach((country) => {
    if (isSearchedFor(country.displayText, searchText)) {
      if (isPreferred(country, preferredCountryCodes)) {
        if (startsWith(country.displayText, searchText)) {
          _preferredCountriesStartsWith.push(country)
        } else {
          _preferredCountries.push(country)
        }
      } else if (startsWith(country.displayText, searchText)) {
        _searchedCountriesStartsWith.push(country)
      } else {
        _searchedCountries.push(country)
      }
    }
  })

  return {
    searchedCountries: [..._searchedCountriesStartsWith, ..._searchedCountries],
    preferredCountries: [
      ..._preferredCountriesStartsWith,
      ..._preferredCountries,
    ],
  }
}
